// Playback clock and mic recording.
import type { Seg, Timeline } from "../engine/timeline";
import type { VoiceSettings } from "../model/voice";
import { VOICE_LATENCY, buildVoiceChain, prepareVoice, type VoiceChain } from "./voice/voice";

// Each speaker's voice filter, and each line's audio URL.
export type VoiceOf = (speaker: string) => VoiceSettings;
export type UrlOf = (seg: Seg) => string;
export const playable = (s: Seg) => s.line.status === "ready" && !!s.line.audio;

// One piece of speech to schedule: who says it, when it starts and ends (episode seconds) and where its audio is.
// Old episodes make these from their timeline's segments; timeline episodes from their spoken lines, which come one after another.
export type AudioClip = { speaker: string; start: number; end: number; url: string };
export const segClips = (tl: Timeline, urlOf: UrlOf): AudioClip[] => tl.segs.filter(playable).map(s => ({ speaker: s.line.speaker, start: s.start, end: s.end, url: urlOf(s) }));

// Web Audio schedules every line sample-accurately; the picture reads its time from the same clock,
// so mouths stay locked to the sound.
class AudioEngine {
  private ctx?: AudioContext;
  private gain?: GainNode;
  private cache = new Map<string, Promise<AudioBuffer>>();
  private sources: AudioBufferSourceNode[] = [];
  private chains: { bus: GainNode; chain: VoiceChain }[] = [];
  muted = false;

  private ensure() {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.gain!.gain.value = this.muted ? 0 : 1;
    return this.ctx;
  }

  load(url: string) {
    let p = this.cache.get(url);
    if (!p) {
      const ctx = this.ensure();
      p = fetch(url).then(r => r.arrayBuffer()).then(b => ctx.decodeAudioData(b));
      this.cache.set(url, p);
    }
    return p;
  }

  setMuted(m: boolean) {
    this.muted = m;
    if (this.gain) this.gain.gain.value = m ? 0 : 1;
  }

  stop() {
    for (const s of this.sources) try { s.stop(); } catch {}
    this.sources = [];
    // Let echo tails ring out briefly before the chains are torn down.
    const old = this.chains;
    this.chains = [];
    setTimeout(() => old.forEach(c => { c.chain.dispose(); c.bus.disconnect(); }), 1500);
  }

  // A bus that runs through a voice chain into the speakers.
  async voiceBus(voice: VoiceSettings) {
    const ctx = this.ensure();
    await prepareVoice(ctx);
    const bus = ctx.createGain();
    const chain = buildVoiceChain(ctx, voice, bus, this.gain!);
    this.chains.push({ bus, chain });
    return { ctx, bus, chain };
  }

  // Plays one buffer through a voice chain (the builder's "try my voice"). The clock is in buffer time
  // and already allows for the chain's latency, so mouths line up with what you hear.
  async playSample(buf: AudioBuffer, voice: VoiceSettings) {
    this.stop();
    const { ctx, bus, chain } = await this.voiceBus(voice);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(bus);
    const t0 = ctx.currentTime + 0.05;
    src.start(t0);
    this.sources.push(src);
    return { chain, clock: () => ctx.currentTime - t0 - VOICE_LATENCY };
  }

  // Plays the episode from `from` to `to` seconds and returns a clock in episode time.
  start(tl: Timeline, from: number, to: number, urlOf: UrlOf, voiceOf: VoiceOf): Promise<() => number> {
    return this.startClips(segClips(tl, urlOf), from, to, voiceOf);
  }

  // The same for any list of clips (timeline episodes). Clips may overlap; each goes through its speaker's voice chain.
  async startClips(all: AudioClip[], from: number, to: number, voiceOf: VoiceOf): Promise<() => number> {
    const ctx = this.ensure();
    this.stop();
    await prepareVoice(ctx);
    const clips = all.filter(c => c.end > from && c.start < to);
    const buffers = await Promise.all(clips.map(c => this.load(c.url).catch(() => null)));
    // One chain per speaker.
    const buses = new Map<string, GainNode>();
    for (const c of clips) {
      if (!buses.has(c.speaker)) buses.set(c.speaker, (await this.voiceBus(voiceOf(c.speaker))).bus);
    }
    const t0 = ctx.currentTime + 0.05;
    clips.forEach((c, i) => {
      const buf = buffers[i];
      if (!buf) return;
      const offset = Math.max(0, from - c.start);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(buses.get(c.speaker)!);
      src.start(t0 + Math.max(0, c.start - from), offset, Math.max(0, Math.min(buf.duration, to - c.start) - offset));
      this.sources.push(src);
    });
    // The chains delay the sound by VOICE_LATENCY, so the picture clock waits the same amount.
    return () => from + Math.max(0, ctx.currentTime - t0 - VOICE_LATENCY);
  }
}
export const engine = new AudioEngine();

export class MicRecorder {
  private stream?: MediaStream;
  private analyser?: AnalyserNode;
  private rec?: MediaRecorder;
  private chunks: Blob[] = [];
  private buf = new Uint8Array(1024);
  startedAt = 0; // performance.now() when the recording really began, for lining up a camera with it

  async open() {
    if (this.stream) return;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(this.stream);
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    src.connect(this.analyser);
  }

  start() {
    if (!this.stream) throw new Error("Mic not open");
    this.chunks = [];
    const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(m => MediaRecorder.isTypeSupported(m));
    this.rec = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    this.rec.ondataavailable = e => e.data.size && this.chunks.push(e.data);
    this.startedAt = performance.now();
    this.rec.onstart = () => { this.startedAt = performance.now(); };
    this.rec.start(250);
  }

  stop(): Promise<Blob> {
    return new Promise(resolve => {
      const rec = this.rec;
      if (!rec || rec.state === "inactive") return resolve(new Blob());
      rec.onstop = () => resolve(new Blob(this.chunks, { type: rec.mimeType || "audio/webm" }));
      rec.stop();
    });
  }

  // 0..1 loudness of the last few milliseconds, for the level meter.
  level() {
    if (!this.analyser) return 0;
    this.analyser.getByteTimeDomainData(this.buf);
    let sum = 0;
    for (const v of this.buf) sum += ((v - 128) / 128) ** 2;
    return Math.min(1, Math.sqrt(sum / this.buf.length) * 4);
  }
}
